'use client';

import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import {
  PageHeader,
  Avatar,
  Tag,
  Banner,
  Dropdown,
  ScheduleCalendar,
  Button as DSButton,
} from '@miso/design-system';
import type { DayData } from '@miso/design-system';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import type { TabPageComponent, TabContext } from '@/entities/tab/model/tab.types';
import { TabToolbar } from '@/widgets/tab/tab-toolbar';
import { TabAwarePageWrapper } from '@/shared/ui/overlay/Modal';
import { useAuth, useAuthSyncState } from '@/entities/auth/model/useAuth';
import { useAtomValue, useSetAtom } from 'jotai';
import { viewModeAtom } from '@/entities/workspace-config';
import { suppressWorkspaceActionFabAtom } from '@/shared/lib/suppressWorkspaceActionFabAtom';
import { useMyPlanner } from '@/features/planner/get-planner/useMyPlanner';
import { usePlannerWorkScheduleDataByMe } from '@/widgets/planner-for-planner';
import { useDockviewApi } from '@/app/providers/DockviewApiProvider';
import { useNavigateTab } from '@/features/tab/tab-history';
import { deserialize } from '@/entities/tab/model/path';
import { Fab, Menu } from '@miso/design-system';
import {
  ThemeSwitcherSubMenu,
  LogoutMenuItem,
  ViewModeSwitcherSubMenu,
} from '@/shared/ui/fab/menu-items';
import MenuIcon from '@mui/icons-material/Menu';
import MapIcon from '@mui/icons-material/Map';
import PersonIcon from '@mui/icons-material/Person';
import EventNoteIcon from '@mui/icons-material/EventNote';

dayjs.locale('ko');

// ── Helpers ───────────────────────────────────────────────────────────────────

function toMonthKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function addMonths(date: Date, delta: number): Date {
  const d = new Date(date);
  d.setDate(1);
  d.setMonth(d.getMonth() + delta);
  return d;
}

function buildMonthOptions(base: Date) {
  const baseYear = base.getFullYear();
  return [-1, 0, 1, 2].map((delta) => {
    const d = addMonths(base, delta);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const label = year !== baseYear ? `${year}년 ${month}월 근무일정` : `${month}월 근무일정`;
    return { value: toMonthKey(d), label };
  });
}

/** WorkScheduleDayData → ScheduleCalendar용 DayData 변환 */
function buildDayData(
  scheduleData: Record<string, { regions: string[]; counsels: number }>,
  monthKey: string
): Record<number, DayData> {
  const result: Record<number, DayData> = {};
  for (const [dateStr, data] of Object.entries(scheduleData)) {
    if (!dateStr.startsWith(monthKey)) continue;
    if (data.regions.length === 0 || data.regions[0] === '-') continue;
    const day = Number(dateStr.split('-')[2]);
    const description =
      data.regions.length === 1 ? data.regions[0] : `${data.regions[0]}+${data.regions.length - 1}`;
    result[day] = {
      description,
      tag: data.counsels > 0 ? `${data.counsels}건` : undefined,
    };
  }
  return result;
}

/** 해당 월의 총 상담 건수 집계 */
function sumMonthCounsels(
  scheduleData: Record<string, { counsels: number }>,
  monthKey: string
): number {
  return Object.entries(scheduleData)
    .filter(([dateStr]) => dateStr.startsWith(monthKey))
    .reduce((sum, [, data]) => sum + (data.counsels ?? 0), 0);
}

/** 플래너 뱃지 레이블 변환 */
function resolveEmploymentLabel(type: string): string {
  const map: Record<string, string> = {
    CONTRACT: '계약직',
    REGULAR: '정규직',
    FREELANCER: '프리랜서',
  };
  return map[type.toUpperCase()] ?? type;
}

function resolveTransportLabel(type: string): string {
  const map: Record<string, string> = {
    CAR: '자차',
    OWN_CAR: '자차',
    PUBLIC: '대중교통',
    PUBLIC_TRANSPORT: '대중교통',
    COMPANY_CAR: '법인차',
  };
  return map[type.toUpperCase()] ?? type;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const PlannerMyProfilePage: TabPageComponent = (props: TabContext) => {
  const { user } = useAuth();
  const { isLoading: isAuthLoading, isInitialized: isAuthInitialized } = useAuthSyncState();
  const viewMode = useAtomValue(viewModeAtom);
  const plannerId = user?.plannerId ?? '';
  const isPlannerMode = user?.role === 'Planner' || viewMode === 'Planner';

  const { planner, isLoading: plannerLoading } = useMyPlanner(
    isAuthInitialized && !isAuthLoading && !!plannerId && isPlannerMode
  );

  const api = useDockviewApi();
  const { navigateTab } = useNavigateTab(api);
  const setSuppressGlobalFab = useSetAtom(suppressWorkspaceActionFabAtom);
  React.useEffect(() => {
    setSuppressGlobalFab(true);
    return () => setSuppressGlobalFab(false);
  }, [setSuppressGlobalFab]);

  const currentMonthKey = toMonthKey(new Date());
  const [selectedMonth, setSelectedMonth] = useState(currentMonthKey);
  const [calendarDate, setCalendarDate] = useState(`${currentMonthKey}-01`);

  const plannerWorkScheduleData = usePlannerWorkScheduleDataByMe(selectedMonth);
  const { scheduleData } = plannerWorkScheduleData;

  const monthOptions = useMemo(
    () => buildMonthOptions(new Date(`${selectedMonth}-01`)),
    [selectedMonth]
  );

  const dayData = useMemo(
    () => buildDayData(scheduleData, selectedMonth),
    [scheduleData, selectedMonth]
  );

  const totalCounsels = useMemo(
    () => sumMonthCounsels(scheduleData, selectedMonth),
    [scheduleData, selectedMonth]
  );

  const nextMonth = useMemo(() => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const nm = (m ?? 1) === 12 ? 1 : (m ?? 1) + 1;
    const ny = (m ?? 1) === 12 ? (y ?? 2026) + 1 : (y ?? 2026);
    return { key: `${ny}-${String(nm).padStart(2, '0')}`, label: nm };
  }, [selectedMonth]);

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    setCalendarDate(`${month}-01`);
  };

  const handleBack = () => {
    navigateTab(deserialize('/workspace/my/planner'));
  };

  // ── Auth guard ─────────────────────────────────────────────────────────────

  React.useEffect(() => {
    if (!isAuthInitialized || isAuthLoading) return;
    if (!isPlannerMode || !plannerId) {
      navigateTab(deserialize(`/404?tabId=${props.tabId}`));
    }
  }, [isAuthInitialized, isAuthLoading, isPlannerMode, plannerId, props.tabId, navigateTab]);

  if (!isAuthInitialized || isAuthLoading) return null;
  if (!isPlannerMode || !plannerId) return null;
  if (plannerLoading || !planner) return null;

  const employmentLabel = resolveEmploymentLabel(planner.employmentType);
  const transportLabel = resolveTransportLabel(planner.transportationType);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <TabToolbar tabId={props.tabId || ''} />
      <TabAwarePageWrapper>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* ── Header ──────────────────────────────────────────────── */}
          <PageHeader title="내 정보" onBack={handleBack} />

          {/* ── Scrollable Content ──────────────────────────────────── */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              padding: '4px 16px 32px',
            }}
          >
            {/* 프로필 헤더 */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Avatar variant="green" size="lg" aria-label={`${planner.name} 플래너`}>
                <span style={{ fontSize: 18, fontWeight: 700 }}>{planner.name.charAt(0)}</span>
              </Avatar>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span
                  style={{
                    fontSize: 24,
                    fontWeight: 700,
                    letterSpacing: '-0.034em',
                    color: '#111827',
                    lineHeight: 1.2,
                  }}
                >
                  {planner.name} 플래너
                </span>
                <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <Tag variant="neutral">{employmentLabel}</Tag>
                  <Tag variant="neutral">{transportLabel}</Tag>
                </Box>
              </Box>
            </Box>

            {/* 이번달 상담 건수 배너 */}
            {totalCounsels > 0 && (
              <Banner onPress={() => {}}>
                이번달에 총 <Banner.Highlight>{totalCounsels}건</Banner.Highlight>의 상담을
                진행했어요.
              </Banner>
            )}

            {/* 근무 캘린더 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Dropdown
                options={monthOptions}
                value={selectedMonth}
                onSelect={handleMonthChange}
                variant="ghost"
                size="s"
              />

              <ScheduleCalendar
                mode="month"
                dayData={dayData}
                date={calendarDate}
                onDatePress={(entry) => setCalendarDate(String(entry.id))}
                toggleBehavior="monthCalendar"
              />

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <DSButton
                  variant="secondary"
                  size="s"
                  shape="rounded"
                  onPress={() => handleMonthChange(nextMonth.key)}
                >
                  {nextMonth.label}월 달력 확인하기
                </DSButton>
              </Box>
            </Box>
          </Box>

          {/* ── FAB 메뉴 — 우측 하단 ────────────────────────────────── */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 24,
              right: 24,
              zIndex: 10,
            }}
          >
            <Fab icon={<MenuIcon />} aria-label="메뉴" placement="top-end">
              <Menu.Section label="플래너">
                <Menu.Item
                  icon={<MapIcon fontSize="small" />}
                  onClick={() => navigateTab(deserialize('/workspace/my/planner'))}
                >
                  상담 지도
                </Menu.Item>
                <Menu.Item
                  icon={<EventNoteIcon fontSize="small" />}
                  onClick={() => navigateTab(deserialize('/workspace/my/counsels'))}
                >
                  상담 목록
                </Menu.Item>
                <Menu.Item icon={<PersonIcon fontSize="small" />} disabled>
                  내 정보
                </Menu.Item>
              </Menu.Section>
              <Menu.Section label="설정">
                <ViewModeSwitcherSubMenu />
                <ThemeSwitcherSubMenu />
                <LogoutMenuItem />
              </Menu.Section>
            </Fab>
          </Box>
        </Box>
      </TabAwarePageWrapper>
    </Box>
  );
};

export default PlannerMyProfilePage;
